import { ReactElement, useState, useEffect } from 'react';
import { useStoreActions, useStoreState } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error';
import Header from './Header';
import Table from './Table';
import Modal from '~/ui/components/common/Modal';
import Loader from '~/ui/components/common/Loader';
import { IPagePermissions } from '../../types';
import styles from './TriagePopup.module.scss';
import { getInitialValues } from './Header/form';
import { TriageType } from '~/services/api/enums';
import { IBloodPressureReading } from '~/services/api/readings/types';

interface IProps {
  permissions: IPagePermissions;
  programId: number;
  triageTypeId: TriageType;
  title: string;
  onClose: () => void;
  setSelectedNoteId: (id: number) => void;
  onBloodPressureUpdated: (data: IBloodPressureReading) => void;
}

const TriagePopup = ({
  permissions,
  programId,
  triageTypeId,
  title,
  onClose,
  setSelectedNoteId,
  onBloodPressureUpdated,
}: IProps): ReactElement => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(getInitialValues());

  const { pagination, triageRequests } = useStoreState(state => state.triage);
  const { onGetTriageRequests, onGetMoreTriageRequests } = useStoreActions(
    actions => actions.triage,
  );
  const showError = useStoreActions(actions => actions.snackbar.showError);

  const payload = { programId, triageTypeId, ...filters };

  const onMount = async () => {
    setLoading(true);
    try {
      await onGetTriageRequests(payload);
      setLoading(false);
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  const onLoadMore = () => {
    onGetMoreTriageRequests(payload);
  };

  useEffect(() => {
    onMount();
  }, [filters]);

  return (
    <Modal open onClose={onClose} className={styles.modal} maxWidth="lg" title={title}>
      <div className={styles.container}>
        <Header
          programId={programId}
          triageTypeId={triageTypeId}
          total={triageRequests.total}
          onSubmit={setFilters}
        />
        {loading ? (
          <Loader />
        ) : (
          <Table
            requests={triageRequests.requests}
            permissions={permissions}
            programId={programId}
            onLoadMore={onLoadMore}
            pagination={pagination}
            setSelectedNoteId={setSelectedNoteId}
            onBloodPressureUpdated={onBloodPressureUpdated}
          />
        )}
      </div>
    </Modal>
  );
};

export default TriagePopup;
